// =============================================================================
// app/src/lib/encomendaDoc.ts — o papel da ENCOMENDA, em duas vias.
//
// POR QUE ESTE ARQUIVO EXISTE (correção de escopo, 19/08/2026)
//
// O balcão vinha emitindo VALE "A VER" para cliente que encomendava peça. Não
// por confusão de conceito: o vale era o único documento impresso que existia
// para pôr na mão do cliente. Faltando papel de encomenda, usou-se o que havia.
//
// Só que os dois são opostos no balanço — o vale é dívida da loja com quem
// devolveu mercadoria; a encomenda é promessa de entregar peça que o cliente
// pode ainda nem ter pago. Encomenda virando vale inflava o passivo de crédito
// e escondia o pedido do lugar onde ele deveria estar. Este documento fecha o
// buraco: a partir dele, encomenda tem papel próprio.
//
// O QUE O PAPEL DIZ EM VOZ ALTA: a situação do pagamento. É a primeira coisa
// que quem entrega precisa saber — PAGO, PAGO EM PARTE (com quanto falta) ou
// NÃO PAGO. Sem isso, entrega-se de graça ou cobra-se duas vezes.
//
// 066 — A ENCOMENDA VIROU LISTA, E O PAPEL CRESCEU COM ELA. Cada peça é uma
// linha nas DUAS vias, e o orçamento de página continua sendo um A4 (o agente
// da loja fotografa exatamente 794×1123 px CSS e corta o que passar). Passando
// de 4 peças o documento entra em modo `denso` — aperta padding, corpo de
// tabela e espaço de assinatura — e assim as duas vias seguem cabendo até
// ~8 peças. Acima disso, quem imprime pelo NAVEGADOR continua vendo tudo (ele
// pagina); é a fila da loja que corta. Mexeu no layout? Meça de novo.
//
// Mesmas duas decisões de segurança do vale (aVerDoc.ts), pelos mesmos motivos:
// TODO campo vindo do banco passa por `escaparHtml()` (a janela nasce de
// about:blank e HERDA a origem do app, onde vive o token de sessão de quem
// imprime), e o documento declara CSP `script-src 'none'` — se um escape
// faltar um dia, o payload vira texto na folha, não código.
// =============================================================================
import { brl, dataBR, dataHoraBR, qtd } from './formato';
import { dataPorExtenso, escaparHtml } from './aVerDoc';
import { totalDaEncomenda } from './encomenda';

/** Uma peça do pedido no papel (066). `descricao` já vem resolvida pela tela
 *  (nome da peça diversa, ou a do catálogo) — o papel não escolhe nome. */
export interface PecaImpressa {
  descricao: string;
  quantidade: number;
  /** O combinado DESTA LINHA. Nulo = a acertar, e sai assim no papel. */
  valor: number | null;
}

/** O que o papel precisa saber. Estrutural de propósito: a tela monta isto de
 *  uma `EncomendaLinha` + os pagamentos, e o teste monta à mão. */
export interface EncomendaImpressa {
  codigo: string;
  cliente_nome: string;
  cliente_contato: string | null;
  /** As peças do pedido (066). Antes era UMA, em `descricao`/`quantidade`. */
  itens: PecaImpressa[];
  observacao: string | null;
  /** 'YYYY-MM-DD' — até quando a peça fica separada/prometida. */
  expira_em: string;
  vendedor: string;
  criada_em: string;
  pagamentos: Array<{ valor: number; forma: string; recebido_em: string }>;
}

export interface SituacaoPagamento {
  /** O que vai na tarja, em caixa alta. */
  tarja: string;
  /** A soma das peças (066). Nulo enquanto alguma linha não tiver valor. */
  total: number | null;
  pago: number;
  falta: number | null;
  quitada: boolean;
}

/**
 * A frase que o dono pediu — "pago", "pago em parte - X valor", "não pago" —
 * derivada da soma dos pagamentos, nunca de uma coluna. Exportada porque a
 * tela mostra a mesma régua em cima do botão: papel e tela discordarem sobre
 * quanto o cliente deve é briga de balcão garantida.
 */
export function situacaoDoPagamento(e: EncomendaImpressa): SituacaoPagamento {
  const pago = e.pagamentos.reduce((soma, p) => soma + Number(p.valor), 0);
  // 066: o combinado é a SOMA das peças, e some inteiro enquanto uma linha
  // estiver sem valor. Derivar aqui (em vez de receber o número pronto) é o
  // que impede o papel de dizer um total e a tela outro.
  const combinado = totalDaEncomenda(e.itens);
  if (combinado === null) {
    return {
      tarja: pago > 0
        ? `VALOR A COMBINAR · JÁ PAGOU ${brl(pago)}`
        : 'VALOR A COMBINAR',
      total: null, pago, falta: null, quitada: false,
    };
  }
  const total = Number(combinado);
  const falta = Math.max(total - pago, 0);
  if (falta < 0.005) return { tarja: `PAGO · ${brl(total)}`, total, pago, falta: 0, quitada: true };
  if (pago > 0) {
    return {
      tarja: `PAGO EM PARTE · ${brl(pago)} DE ${brl(total)} — FALTA ${brl(falta)}`,
      total, pago, falta, quitada: false,
    };
  }
  return { tarja: `NÃO PAGO · ${brl(total)} NA RETIRADA`, total, pago, falta, quitada: false };
}

// O SÍMBOLO SAI EM PRETO NO PAPEL, e isso é decisão de operação (20/08/2026).
// Na tela e no site ele é o vermelho da marca (#D90429, regra 10). Aqui não:
// UM pixel colorido faz a HP jato de tinta trocar para o modo em cores, e a
// página que sai em segundos passa a levar quase um minuto — com o cliente
// esperando no balcão. O agente ainda manda `print-color-mode: monochrome` por
// IPP; este preto é o cinto além do suspensório (documento que a loja imprime
// fora do agente também sai rápido).
const S_LOGO = `<svg viewBox="0 0 738 502" width="34" style="vertical-align:middle" xmlns="http://www.w3.org/2000/svg"><path fill="#111111" d="M265.16 197.096L716.099 197.133C710.828 236.013 703.583 277.477 697.547 316.427L691.319 357.303C688.727 374.581 687.153 388.315 681.173 404.926C673.717 426.092 661.62 445.323 645.765 461.208C626.687 480.431 602.358 493.601 575.825 499.063C558.852 502.518 534.196 501.482 516.359 501.482L0 501.506C6.45146 483.313 20.0753 438.942 29.209 424.596C33.8706 417.227 39.9357 410.846 47.0599 405.817C68.5016 390.473 90.0878 393.236 114.993 393.246L467.663 393.298C480.383 393.309 491.548 393.616 505.058 391.956C536.745 388.419 573.091 341.625 550.341 312.787C542.231 302.501 520.482 305.103 508.558 305.102L240.273 305.049L265.16 197.096Z"/><path fill="#111111" d="M195.821 0.543453C213.607 -0.496412 240.032 0.274996 258.347 0.279316L737.602 0.30894L726.382 34.5152C715.525 67.7255 704.255 100.888 663.231 107.702C652.029 109.563 638.877 108.711 627.422 108.697L297.094 108.625C283.826 108.599 258.16 107.708 246.463 110.265C234.27 113.024 222.913 118.655 213.336 126.689C202.159 136.104 193.793 148.416 189.157 162.274C184.055 177.316 183.118 204.508 181.773 221.171L174.91 305.148L27.0847 305.094C27.9432 296.569 30.2866 284.133 31.7117 275.497L41.2102 218.07L50.7884 158.008C53.5367 140.681 55.7487 122.604 61.4157 106.006C68.0344 86.9096 78.1359 69.2054 91.208 53.7901C117.493 22.6886 155.205 3.49334 195.821 0.543453Z"/></svg>`;

/**
 * QUANTO APERTAR O PAPEL, pelo tamanho da lista. Os cortes foram MEDIDOS no
 * navegador, num A4 de 794×1123 px CSS — que é a janela exata do agente da
 * loja (infra/impressora/agente.py) —, e não estimados:
 *
 *    1 peça      → normal      1.082 px
 *    2 a 6       → denso         944 a 1.060 px
 *    7 a 11      → apertado      994 a ~1.100 px
 *
 * De 12 peças em diante o papel passa do A4 e a fila corta a via da loja. A
 * janela do navegador continua mostrando tudo (ela pagina); encomenda desse
 * tamanho não apareceu no balcão ainda, e se aparecer o caminho é quebrar em
 * duas folhas, não encolher mais a letra.
 *
 * DE QUEBRA, ISTO CONSERTA UM ESTOURO QUE JÁ EXISTIA: o papel de UMA peça
 * media 1.153 px antes desta migração — 30 px além do A4 —, ou seja, a fila já
 * vinha cortando o rodapé da via da loja sem ninguém notar.
 */
export function classeDeDensidade(pecas: number): string {
  if (pecas > 6) return 'denso apertado';
  if (pecas > 1) return 'denso';
  return '';
}

export interface OpcoesEncomendaDoc {
  /** Momento da impressão — injetável para o teste. */
  agora?: Date;
  /** false na 2ª via impressa depois (marca "REIMPRESSÃO"). */
  emissao?: boolean;
}

function via(
  e: EncomendaImpressa, sit: SituacaoPagamento, rotuloVia: string, daLoja: boolean,
  cfg: Required<OpcoesEncomendaDoc>,
): string {
  const pagos = e.pagamentos.length > 0
    ? `<table class="pagos">
        <tr><th colspan="3">O que já foi pago</th></tr>
        ${e.pagamentos.map((p) => `<tr>
          <td>${escaparHtml(dataBR(p.recebido_em))}</td>
          <td>${escaparHtml(p.forma)}</td>
          <td class="dir">${brl(Number(p.valor))}</td>
        </tr>`).join('')}
      </table>`
    : '';

  return `
  <div class="via">
    <header>
      <div class="marca">${S_LOGO}<span class="nome">SERGINHO</span><span class="sub">AUTO PEÇAS</span></div>
      <div class="doc">
        <span class="titulo">ENCOMENDA${cfg.emissao ? '' : ' · REIMPRESSÃO'}</span>
        <span class="codigo">${escaparHtml(e.codigo)}</span>
      </div>
    </header>

    <p class="tarja${sit.quitada ? ' quitada' : ''}">${escaparHtml(sit.tarja)}</p>

    <table class="itens">
      <tr><th>Peça</th><th class="cen">Qtd.</th><th class="dir">Valor</th></tr>
      ${e.itens.map((i) => `<tr>
        <td><strong>${escaparHtml(i.descricao)}</strong></td>
        <td class="cen">${escaparHtml(qtd(i.quantidade))}</td>
        <td class="dir">${i.valor === null ? 'a combinar' : brl(Number(i.valor))}</td>
      </tr>`).join('')}
      ${e.itens.length > 1 && sit.total !== null
        ? `<tr class="soma"><td colspan="2">Total</td>
             <td class="dir">${brl(sit.total)}</td></tr>`
        : ''}
    </table>

    <table>
      <tr><td class="rot">Cliente</td><td>${escaparHtml(e.cliente_nome)}</td></tr>
      ${e.cliente_contato ? `<tr><td class="rot">Contato</td><td>${escaparHtml(e.cliente_contato)}</td></tr>` : ''}
      <tr><td class="rot">Pedido em</td><td>${escaparHtml(dataBR(e.criada_em))} · ${escaparHtml(e.vendedor)}</td></tr>
      <tr><td class="rot">Previsão</td><td>${escaparHtml(dataBR(`${e.expira_em}T12:00:00`))}</td></tr>
      ${e.observacao ? `<tr><td class="rot">Observação</td><td>${escaparHtml(e.observacao)}</td></tr>` : ''}
    </table>

    ${pagos}

    <p class="termos">
      Este documento registra um <strong>pedido de peça</strong> — não é vale de
      crédito e não vale como dinheiro. A peça fica reservada para o cliente
      identificado até a data de previsão acima.
      ${sit.falta !== null && sit.falta > 0
        ? `<strong>Saldo de ${brl(sit.falta)} a pagar na retirada.</strong>`
        : sit.quitada ? '<strong>Pedido pago; basta retirar.</strong>' : ''}
      Apresente esta via com o código <strong>${escaparHtml(e.codigo)}</strong> para retirar.
    </p>

    <p class="local-data">Rio Grande/RS, ${escaparHtml(dataPorExtenso(e.criada_em))}.</p>

    ${daLoja
      ? `<div class="retirada">
          <span class="rot">Retirado por</span>
          <span class="linha"></span>
          <span class="rot">Data</span>
          <span class="linha curta"></span>
        </div>`
      : `<div class="assinaturas">
          <div><span></span><strong>Serginho Autopeças</strong><em>${escaparHtml(e.vendedor)}</em></div>
          <div><span></span><strong>${escaparHtml(e.cliente_nome)}</strong><em>Cliente</em></div>
        </div>`}

    <p class="rodape">${escaparHtml(rotuloVia)} · ${escaparHtml(e.codigo)}${
      cfg.emissao ? '' : ` · reimpresso em ${escaparHtml(dataHoraBR(cfg.agora.toISOString()))}`
    }</p>
  </div>`;
}

/** Monta o HTML completo (duas vias numa folha). Separado da impressão para
 *  poder ser testado — e é o MESMO HTML que vai para a fila da loja. */
export function montarDocumentoEncomenda(
  e: EncomendaImpressa, opcoes: OpcoesEncomendaDoc = {},
): string {
  const cfg: Required<OpcoesEncomendaDoc> = {
    agora: opcoes.agora ?? new Date(),
    emissao: opcoes.emissao ?? true,
  };
  const sit = situacaoDoPagamento(e);

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy"
      content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'none'; form-action 'none'">
<title>${escaparHtml(e.codigo)} — Encomenda</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  /* MEDIDAS SÃO ORÇAMENTO DE PÁGINA, NÃO GOSTO: duas vias num A4 (1123 px CSS).
     O agente de impressão fotografa exatamente um A4 e corta o que passar.
     Acrescentou linha? Meça de novo. */
  body { font-family: Arial, Helvetica, sans-serif; color: #111; padding: 5mm; }
  .via { border: 2px solid #111; padding: 4mm; margin-bottom: 3mm; page-break-inside: avoid; }
  header { display: flex; justify-content: space-between; align-items: center;
           border-bottom: 2px solid #111; padding-bottom: 2.5mm; margin-bottom: 3mm; }
  .marca .nome { font-weight: 900; font-style: italic; font-size: 18px; margin-left: 6px; letter-spacing: .5px; }
  .marca .sub { font-size: 9px; letter-spacing: 2px; margin-left: 6px; color: #555; }
  .doc { text-align: right; }
  .doc .titulo { display: block; font-size: 11px; letter-spacing: 2px; }
  .doc .codigo { display: block; font-weight: 900; font-size: 18px; font-family: monospace; }
  /* A tarja é o motivo do papel existir: quem entrega lê ela primeiro. */
  .tarja { border: 3px solid #111; background: #111; color: #fff; text-align: center;
           font-weight: 900; font-size: 14px; letter-spacing: 2px; padding: 2.5mm; margin-bottom: 4mm; }
  .tarja.quitada { background: #fff; color: #111; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; }
  td, th { padding: 1mm 0; font-size: 12.5px; border-bottom: 1px solid #ddd; text-align: left; }
  td.rot { width: 32mm; color: #555; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; }
  /* A LISTA DE PEÇAS (066). Ela é a razão de o papel crescer — ver o orçamento
     de página no topo deste bloco. */
  .itens th { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #555;
              border-bottom: 1px solid #111; }
  .itens td { font-size: 12px; }
  .itens .soma td { font-weight: 900; border-top: 1px solid #111; border-bottom: none; }
  .pagos th { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #555; }
  .pagos td { font-size: 11.5px; }
  .cen { text-align: center; }
  .dir { text-align: right; font-weight: 700; }
  .termos { font-size: 10px; line-height: 1.4; color: #333; margin-bottom: 3mm; }
  .local-data { font-size: 10.5px; text-align: right; margin-bottom: 8mm; }
  .assinaturas { display: flex; gap: 12mm; margin-bottom: 2.5mm; }
  .assinaturas div { flex: 1; text-align: center; font-size: 11px; }
  .assinaturas span { display: block; border-top: 1px solid #111; margin-bottom: 1.5mm; padding-top: 1.5mm; }
  .assinaturas strong { display: block; font-size: 11px; }
  .assinaturas em { display: block; font-style: normal; font-size: 9.5px; color: #555; }
  /* A via da loja não pede assinatura de contrato — pede o RECIBO da entrega,
     preenchido no dia em que o cliente vier buscar. */
  .retirada { display: flex; align-items: flex-end; gap: 4mm; margin-bottom: 2.5mm; }
  .retirada .rot { font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: #555; }
  .retirada .linha { flex: 1; border-bottom: 1px solid #111; height: 7mm; }
  .retirada .linha.curta { flex: 0 0 30mm; }
  .rodape { font-size: 9px; color: #777; text-align: right; }
  /* PEDIDO GRANDE: cada peça acrescenta uma linha nas DUAS vias, e o A4 é o
     que é. A partir da 2ª peça o papel aperta o que dá para apertar — o respiro
     dos termos, o corpo das tabelas e o espaço de assinatura — em vez de
     empurrar a via da loja para fora da folha, que é o que o agente corta. */
  .denso .via { padding: 3mm; margin-bottom: 2mm; }
  .denso .tarja { font-size: 12px; padding: 1.5mm; margin-bottom: 2mm; }
  .denso td, .denso th { padding: 0.6mm 0; font-size: 11px; }
  .denso .termos { font-size: 9px; margin-bottom: 2mm; }
  .denso .local-data { margin-bottom: 4mm; }
  .denso .assinaturas, .denso .retirada { margin-bottom: 1.5mm; }
  .denso .retirada .linha { height: 5mm; }
  .denso table { margin-bottom: 2mm; }
  /* LISTA LONGA: encolhe a LISTA, que é o que cresce, e o que sobrou de respiro
     nas bordas. O corpo do documento (tarja, termos, assinatura) fica onde
     está: é o que o cliente lê e assina. */
  .apertado .itens td, .apertado .itens th { font-size: 9.5px; padding: 0.15mm 0; }
  .apertado .via { padding: 2.5mm; margin-bottom: 1.5mm; }
  .apertado .local-data { margin-bottom: 2.5mm; }
  .apertado .pagos td, .apertado .pagos th { font-size: 9.5px; padding: 0.15mm 0; }
  .apertado .retirada .linha { height: 4mm; }
  @media print { body { padding: 0; } }
</style></head>
<body class="${classeDeDensidade(e.itens.length)}">
  ${via(e, sit, '1ª via — cliente', false, cfg)}
  ${via(e, sit, '2ª via — loja', true, cfg)}
</body></html>`;
}

export function imprimirEncomenda(
  e: EncomendaImpressa, opcoes: OpcoesEncomendaDoc = {},
): void {
  const janela = window.open('', '_blank', 'width=800,height=900');
  if (!janela) return;
  janela.document.write(montarDocumentoEncomenda(e, opcoes));
  janela.document.close();
  janela.focus();
  janela.print();
}
